/**
 * Simulated GET /api/qb-accounting — the Financials tab and the Company
 * Overview KPI band.
 *
 * Shape is `AccountingStatus` from src/lib/api/qbFinancials.ts, field for field.
 *
 * Derived from the snapshot
 *   • P&L income  — job_invoices.invoice_subtotal for the period, allocated to
 *     the real chart of accounts in public/data/budget-2026.json by division.
 *   • AR aging    — v_jobs_full rows with balance_due > 0, bucketed by CALENDAR
 *     days since invoiced_at (current / 1-30 / 31-60 / 61-90 / 91+).
 *   • Open invoices — the same rows, each carrying `daysSinceInvoice`, so a
 *     Weighted Days Outstanding of Σ(days × balance) ÷ Σ(balance) reproduces
 *     the Collections figure exactly.
 *   • Write-downs — each "Sales Discount" account keeps the ratio the budget
 *     file gives it against its parent income account.
 *
 * Generated
 *   • COGS / expense actuals (see finance.ts) and the bank + credit-card
 *     balances. The bank list runs through the owner's exclusion rule: account
 *     numbers 1050, 1055, 1022 and 2509 and anything matching /member
 *     distribution/i are dropped; "1044 BofA Tax Acct" is kept.
 */
import { loadTable } from '~/demo/data';
import { jitter, once } from './prng';
import {
  calendarDaysSince, monthRange, nyDate, num, pnlAccountsFor, revenueModel,
  revenueOver, round2, str, type PnlAccounts, type PnlLine,
} from './finance';

const SOURCE = 'demo-simulated';

/** Cash/credit accounts the owner's spec excludes from the cash position. */
const EXCLUDED_ACCOUNT_NUMBERS = ['1050', '1055', '1022', '2509'];
const EXCLUDED_ACCOUNT_NAME = /member distribution/i;

interface PnlNode {
  name: string; amount: number; type: 'section' | 'summary' | 'leaf';
  group?: string | null; children: PnlNode[];
}

function leaves(lines: PnlLine[], group: string): PnlNode[] {
  return lines.map((l) => ({ name: l.name, amount: l.actual, type: 'leaf' as const, group, children: [] }));
}

function summarize(
  accounts: PnlAccounts,
  byDivision: { name: string; amount: number }[],
) {
  const totalIncome = accounts.totals.income;
  const totalCogs = accounts.totals.cogs;
  const totalExpenses = accounts.totals.expenses;
  const grossProfit = round2(totalIncome - totalCogs);
  const netIncome = round2(grossProfit - totalExpenses);
  const writeDownTotal = round2(accounts.discounts.reduce((s, l) => s + l.actual, 0));

  const sections: PnlNode[] = [
    { name: 'Income', amount: totalIncome, type: 'section', group: 'Income', children: leaves(accounts.income, 'Income') },
    { name: 'Cost of Goods Sold', amount: totalCogs, type: 'section', group: 'COGS', children: leaves(accounts.cogs, 'COGS') },
    { name: 'Gross Profit', amount: grossProfit, type: 'summary', group: 'Income', children: [] },
    { name: 'Expenses', amount: totalExpenses, type: 'section', group: 'Expenses', children: leaves(accounts.expenses, 'Expenses') },
    { name: 'Net Income', amount: netIncome, type: 'summary', group: 'Income', children: [] },
  ];

  return {
    totalIncome, totalCogs, totalExpenses, netIncome, grossProfit,
    grossMarginPct: totalIncome > 0 ? round2((grossProfit / totalIncome) * 100) : 0,
    byDivision,
    sections,
    writeDowns: {
      total: writeDownTotal,
      grossInvoiced: accounts.grossInvoiced,
      pctOfGross: accounts.grossInvoiced > 0 ? round2(Math.abs(writeDownTotal / accounts.grossInvoiced) * 100) : 0,
      breakdown: accounts.discounts.map((l) => ({ name: l.name, amount: l.actual })),
    },
  };
}

async function divisionBreakdown(months: string[]): Promise<{ name: string; amount: number }[]> {
  const model = await revenueModel();
  const { byDivision } = revenueOver(model, months);
  return [...byDivision.entries()]
    .map(([code, amount]) => ({ name: model.divisionLabels.get(code) ?? code, amount: round2(amount) }))
    .sort((a, b) => b.amount - a.amount);
}

/** Bank + credit-card balances, scaled off the trailing revenue run-rate. */
function cashAccounts(monthlyRevenue: number) {
  const candidates: { name: string; type: 'bank' | 'credit_card'; weight: number }[] = [
    { name: '1000 BofA Operating', type: 'bank', weight: 0.62 },
    { name: '1010 BofA Payroll', type: 'bank', weight: 0.18 },
    { name: '1022 Member Distribution Reserve', type: 'bank', weight: 0.2 },
    { name: '1044 BofA Tax Acct', type: 'bank', weight: 0.21 },
    { name: '1050 Owner Draw Clearing', type: 'bank', weight: 0.1 },
    { name: '1055 Member Distribution', type: 'bank', weight: 0.1 },
    { name: '1060 Chase Money Market', type: 'bank', weight: 0.34 },
    { name: '2010 Amex Business Platinum', type: 'credit_card', weight: -0.11 },
    { name: '2020 Chase Ink Fleet Card', type: 'credit_card', weight: -0.05 },
    { name: '2509 Member Distribution Payable', type: 'credit_card', weight: -0.08 },
  ];

  const accounts = candidates
    .filter((a) => {
      const number = a.name.split(' ')[0];
      return !EXCLUDED_ACCOUNT_NUMBERS.includes(number) && !EXCLUDED_ACCOUNT_NAME.test(a.name);
    })
    .map((a) => ({
      name: a.name,
      balance: round2(monthlyRevenue * a.weight * jitter(`cash|${a.name}`, 0.18)),
      type: a.type,
    }));

  return { accounts, total: round2(accounts.reduce((s, a) => s + a.balance, 0)) };
}

export const simulatedAccounting = once(async () => {
  const now = new Date();
  const today = nyDate(now);
  const year = Number(today.slice(0, 4));
  const monthKey = today.slice(0, 7);

  const ytdMonths = monthRange(`${year}-01`, monthKey);
  const mtdMonths = [monthKey];
  const priorMonths = monthRange(`${year - 1}-01`, `${year - 1}-${monthKey.slice(5)}`);

  const [ytdAcc, mtdAcc, priorAcc, ytdCashAcc, mtdCashAcc] = await Promise.all([
    pnlAccountsFor(ytdMonths, 'ytd'),
    pnlAccountsFor(mtdMonths, 'mtd'),
    pnlAccountsFor(priorMonths, 'prior-ytd'),
    pnlAccountsFor(ytdMonths, 'ytd', { cash: true }),
    pnlAccountsFor(mtdMonths, 'mtd', { cash: true }),
  ]);
  const [ytdDiv, mtdDiv, priorDiv] = await Promise.all([
    divisionBreakdown(ytdMonths), divisionBreakdown(mtdMonths), divisionBreakdown(priorMonths),
  ]);

  const ytd = summarize(ytdAcc, ytdDiv);
  const mtd = summarize(mtdAcc, mtdDiv);
  const priorYearYtd = summarize(priorAcc, priorDiv);
  const ytdCash = summarize(ytdCashAcc, ytdDiv);
  const mtdCash = summarize(mtdCashAcc, mtdDiv);
  const yoyDeltaPct = priorYearYtd.totalIncome > 0
    ? round2(((ytd.totalIncome - priorYearYtd.totalIncome) / priorYearYtd.totalIncome) * 100)
    : 0;

  // ── AR: real balances, real invoice dates, calendar-day buckets.
  const jobs = await loadTable('v_jobs_full');
  const open = jobs
    .filter((j) => num(j.balance_due) > 0 && str(j.invoiced_at))
    .map((j) => ({
      job_number: str(j.job_number),
      customer: str(j.customer_name),
      division: str(j.division),
      balance: round2(num(j.balance_due)),
      invoiced: round2(num(j.total_invoiced)),
      invoicedAt: str(j.invoiced_at),
      daysSinceInvoice: calendarDaysSince(str(j.invoiced_at), now),
    }))
    .sort((a, b) => b.balance - a.balance);

  const buckets = { current: 0, '1-30': 0, '31-60': 0, '61-90': 0, '91+': 0 };
  for (const inv of open) {
    const d = inv.daysSinceInvoice;
    if (d <= 0) buckets.current += inv.balance;
    else if (d <= 30) buckets['1-30'] += inv.balance;
    else if (d <= 60) buckets['31-60'] += inv.balance;
    else if (d <= 90) buckets['61-90'] += inv.balance;
    else buckets['91+'] += inv.balance;
  }
  for (const k of Object.keys(buckets) as (keyof typeof buckets)[]) buckets[k] = round2(buckets[k]);
  const totalOutstanding = round2(open.reduce((s, i) => s + i.balance, 0));

  const monthlyRevenue = ytdMonths.length ? ytd.totalIncome / ytdMonths.length : 0;
  const nowIso = now.toISOString();

  const payload = {
    asOf: nowIso,
    period: {
      ytdStart: `${year}-01-01`, ytdEnd: today,
      mtdStart: `${monthKey}-01`, mtdEnd: today,
      priorYearYtdStart: `${year - 1}-01-01`,
      priorYearYtdEnd: `${year - 1}${today.slice(4)}`,
    },
    pnl: { ytd, mtd, priorYearYtd, ytdCash, mtdCash, yoyDeltaPct },
    ar: { totalOutstanding, buckets, asOf: today },
    cash: cashAccounts(monthlyRevenue),
    /** Extra, ignored by the UI: the rows behind the AR buckets. */
    openInvoices: open,
    weightedDaysOutstanding: totalOutstanding > 0
      ? round2(open.reduce((s, i) => s + i.daysSinceInvoice * i.balance, 0) / totalOutstanding)
      : 0,
    simulated: true,
  };

  return {
    ok: true,
    qb_configured: true,
    simulated: true,
    demo_note:
      'P&L income is real job_invoices subtotals mapped onto the budget-2026.json chart of accounts by division; '
      + 'COGS/expense actuals are generated from each account\'s budgeted share; AR aging is derived from v_jobs_full '
      + 'balance_due bucketed by calendar days since invoiced_at; bank balances are generated off the revenue run-rate.',
    snapshot: {
      period_start: `${year}-01-01`,
      period_end: today,
      payload,
      updated_at: nowIso,
      source: SOURCE,
    },
  };
});

/** POST /api/qb-accounting — the Refresh button. Nothing to pull; just ack. */
export async function simulatedAccountingSync() {
  const status = await simulatedAccounting();
  return {
    ok: true,
    simulated: true,
    asOf: status.snapshot.payload.asOf,
    demo_note: 'Refresh is a no-op in the demo — the figures are regenerated deterministically from the snapshot.',
  };
}
